import React, { useState } from 'react';

/**
 * FunctionPrismVisualization - Modern interactive visualization showing how different
 * teams/functions use PRISM data. Features glass-morphism prism, flowing energy rays,
 * and smooth animations.
 * Layout: Buttons LEFT | Prism CENTER | Use cases RIGHT
 */
export const FunctionPrismVisualization = () => {
  const [selectedFunction, setSelectedFunction] = useState(null);

  // Function definitions with colors and use cases (using brand colors)
  const functions = {
    clinical: {
      name: 'Clinical Development & Operations',
      color: '#FABEC0',
      lightColor: 'rgba(250, 190, 192, 0.35)',
      gradientEnd: '#F7C08A',
      useCases: [
        'Trial design insights',
        'Endpoint prioritization',
        'Recruitment barriers',
        'Protocol burden',
      ],
    },
    medical: {
      name: 'Medical Affairs',
      color: '#F7C08A',
      lightColor: 'rgba(247, 192, 138, 0.35)',
      gradientEnd: '#FBF4BC',
      useCases: [
        'Real-world evidence',
        'Unmet needs',
        'Treatment patterns',
        'Educational gaps',
      ],
    },
    patientsupport: {
      name: 'Patient Support Programs',
      color: '#E8D477',
      lightColor: 'rgba(251, 244, 188, 0.4)',
      gradientEnd: '#FABEC0',
      useCases: [
        'Adherence challenges',
        'Support preferences',
        'Care coordination',
        'Access barriers',
      ],
    },
    heor: {
      name: 'HEOR and PCOR',
      color: '#FABEC0',
      lightColor: 'rgba(250, 190, 192, 0.35)',
      gradientEnd: '#F7C08A',
      useCases: [
        'Outcomes that matter',
        'Quality of life',
        'Economic burden',
        'Treatment value',
      ],
    },
    commercial: {
      name: 'Commercial and Brand Teams',
      color: '#F7C08A',
      lightColor: 'rgba(247, 192, 138, 0.35)',
      gradientEnd: '#FABEC0',
      useCases: [
        'Patient journey mapping',
        'Brand perception',
        'Channel preferences',
        'Market access',
      ],
    },
    bizdev: {
      name: 'Business Development & Intelligence',
      color: '#E8D477',
      lightColor: 'rgba(251, 244, 188, 0.4)',
      gradientEnd: '#F7C08A',
      useCases: [
        'Competitive landscape',
        'Market sizing',
        'Patient segmentation',
        'Trend analysis',
      ],
    },
    datascience: {
      name: 'Data Science, AI & Analytics',
      color: '#FABEC0',
      lightColor: 'rgba(250, 190, 192, 0.35)',
      gradientEnd: '#FBF4BC',
      useCases: [
        'Training data',
        'NLP-ready text',
        'Structured insights',
        'Model validation',
      ],
    },
    innovation: {
      name: 'Innovation & Digital Health',
      color: '#F7C08A',
      lightColor: 'rgba(247, 192, 138, 0.35)',
      gradientEnd: '#FABEC0',
      useCases: [
        'Digital health needs',
        'Technology adoption',
        'Care delivery gaps',
        'Patient preferences',
      ],
    },
  };

  const currentFunction = selectedFunction ? functions[selectedFunction] : null;

  // SVG dimensions
  const svgWidth = 550;
  const svgHeight = 420;

  // Prism geometry - centered in SVG
  const prismCenter = { x: svgWidth * 0.5, y: svgHeight * 0.5 };
  const prismWidth = 130;
  const prismHeight = 220;

  // Prism vertices
  const apex = { x: prismCenter.x, y: prismCenter.y - prismHeight * 0.45 };
  const bottomLeft = { x: prismCenter.x - prismWidth * 0.5, y: prismCenter.y + prismHeight * 0.35 };
  const bottomRight = { x: prismCenter.x + prismWidth * 0.4, y: prismCenter.y + prismHeight * 0.45 };
  const backRight = { x: prismCenter.x + prismWidth * 0.55, y: prismCenter.y + prismHeight * 0.15 };

  return (
    <div style={{ width: '100%', maxWidth: '1000px', margin: '0 auto' }}>
      <style>{`
        /* Prism floating animation */
        @keyframes prismFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }

        /* Subtle rotation wobble */
        @keyframes prismWobble {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(0.5deg); }
          75% { transform: rotate(-0.5deg); }
        }

        /* Glass shimmer effect */
        @keyframes glassShimmer {
          0% { opacity: 0.3; }
          50% { opacity: 0.6; }
          100% { opacity: 0.3; }
        }

        /* Energy flow along rays */
        @keyframes energyFlow {
          0% { stroke-dashoffset: 30; }
          100% { stroke-dashoffset: 0; }
        }

        /* Ray drawing animation */
        @keyframes rayDraw {
          0% { stroke-dashoffset: 250; opacity: 0; }
          20% { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 1; }
        }

        /* Input beam pulse */
        @keyframes beamPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }

        /* Input beam draw */
        @keyframes beamDraw {
          0% { stroke-dashoffset: 200; opacity: 0; }
          30% { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 1; }
        }

        /* Use case slide up */
        @keyframes slideUp {
          0% { opacity: 0; transform: translateY(15px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        /* Button accent grow */
        @keyframes accentGrow {
          0% { transform: scaleY(0); }
          100% { transform: scaleY(1); }
        }

        /* Prism glow pulse */
        @keyframes glowPulse {
          0%, 100% { filter: drop-shadow(0 0 15px var(--glow-color)) drop-shadow(0 4px 20px rgba(0,0,0,0.1)); }
          50% { filter: drop-shadow(0 0 25px var(--glow-color)) drop-shadow(0 4px 20px rgba(0,0,0,0.15)); }
        }

        .prism-container {
          animation: prismFloat 4s ease-in-out infinite;
        }

        .prism-container.active {
          animation: prismFloat 4s ease-in-out infinite, glowPulse 2s ease-in-out infinite;
        }

        .prism-container.inactive {
          filter: drop-shadow(0 4px 15px rgba(0,0,0,0.08));
          opacity: 0.7;
        }

        .glass-shimmer {
          animation: glassShimmer 3s ease-in-out infinite;
        }

        .energy-ray {
          stroke-dasharray: 8 4;
          animation: rayDraw 0.6s ease-out forwards, energyFlow 0.8s linear infinite 0.6s;
        }

        .input-beam {
          stroke-dasharray: 200;
          animation: beamDraw 0.5s ease-out forwards;
        }

        .input-beam-glow {
          animation: beamPulse 1.5s ease-in-out infinite 0.5s;
        }

        .use-case-card {
          animation: slideUp 0.4s ease-out forwards;
          opacity: 0;
        }

        .func-btn {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
          padding: 10px 16px;
          border: none;
          border-radius: 10px;
          background: #fff;
          font-size: 13px;
          font-weight: 600;
          color: #444;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          text-align: left;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          overflow: hidden;
        }

        .func-btn::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: var(--accent-color);
          transform: scaleY(0);
          transform-origin: center;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 0 4px 4px 0;
        }

        .func-btn:hover {
          background: #fafafa;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transform: translateX(4px);
        }

        .func-btn:hover::before {
          transform: scaleY(0.6);
        }

        .func-btn.selected {
          background: var(--accent-color);
          color: #fff;
          box-shadow: 0 4px 16px var(--shadow-color);
          transform: translateX(4px);
        }

        .func-btn.selected::before {
          background: rgba(255,255,255,0.3);
          transform: scaleY(1);
        }

        .func-btn.dimmed {
          opacity: 0.35;
          transform: scale(0.98);
        }

        .func-btn .btn-icon {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--accent-color);
          margin-right: 12px;
          transition: all 0.3s ease;
        }

        .func-btn.selected .btn-icon {
          background: rgba(255,255,255,0.9);
          box-shadow: 0 0 8px rgba(255,255,255,0.5);
        }

        @media (max-width: 900px) {
          .func-btn {
            padding: 12px 16px;
            font-size: 14px;
          }
        }

        @media (max-width: 768px) {
          .prism-viz-grid {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
          .prism-viz-buttons {
            flex-direction: row !important;
            flex-wrap: wrap !important;
            justify-content: center !important;
          }
          .func-btn {
            flex: 0 0 auto;
            width: auto;
            padding: 10px 16px;
          }
          .prism-viz-svg-container {
            order: -1;
          }
          .prism-viz-usecases {
            flex-direction: row !important;
            flex-wrap: wrap !important;
            justify-content: center !important;
          }
        }
      `}</style>

      <div
        className="prism-viz-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '260px 1fr 230px',
          gap: '24px',
          alignItems: 'center',
          minHeight: '500px',
        }}
      >
        {/* LEFT: Function buttons */}
        <div className="prism-viz-buttons" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <p
            style={{
              fontSize: '12px',
              color: '#999',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: '500',
            }}
          >
            Select a function
          </p>
          {Object.entries(functions).map(([key, func]) => (
            <button
              key={key}
              className={`func-btn ${
                selectedFunction === key
                  ? 'selected'
                  : selectedFunction
                    ? 'dimmed'
                    : ''
              }`}
              style={{
                '--accent-color': func.color,
                '--shadow-color': func.lightColor,
              }}
              onClick={() => setSelectedFunction(selectedFunction === key ? null : key)}
            >
              <span className="btn-icon" />
              {func.name}
            </button>
          ))}
        </div>

        {/* CENTER: Prism SVG */}
        <div className="prism-viz-svg-container" style={{ display: 'flex', justifyContent: 'center' }}>
          <svg
            width="100%"
            height={svgHeight}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            style={{ maxWidth: svgWidth, overflow: 'visible' }}
          >
            <defs>
              {/* Glass gradient for front face */}
              <linearGradient id="glassFront" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
                <stop offset="30%" stopColor="#F5E6DC" stopOpacity="0.7" />
                <stop offset="60%" stopColor="#F5D7C8" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#F7C08A" stopOpacity="0.5" />
              </linearGradient>

              {/* Glass gradient for right face */}
              <linearGradient id="glassRight" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F7C08A" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#D4A5A5" stopOpacity="0.3" />
              </linearGradient>

              {/* Highlight gradient */}
              <linearGradient id="highlight" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>

              {/* Dynamic ray gradient */}
              {currentFunction && (
                <linearGradient id="rayGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={currentFunction.color} stopOpacity="0.9" />
                  <stop offset="50%" stopColor={currentFunction.gradientEnd} stopOpacity="0.7" />
                  <stop offset="100%" stopColor={currentFunction.color} stopOpacity="0.4" />
                </linearGradient>
              )}

              {/* Beam gradient */}
              {currentFunction && (
                <linearGradient id="beamGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={currentFunction.color} stopOpacity="0.3" />
                  <stop offset="50%" stopColor={currentFunction.color} stopOpacity="1" />
                  <stop offset="100%" stopColor={currentFunction.gradientEnd} stopOpacity="0.8" />
                </linearGradient>
              )}

              {/* Glow filter */}
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              {/* Soft shadow */}
              <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.15" />
              </filter>
            </defs>

            {/* Input beam with glow */}
            {selectedFunction && (
              <g>
                {/* Glow layer */}
                <line
                  className="input-beam-glow"
                  x1={30}
                  y1={prismCenter.y}
                  x2={prismCenter.x - prismWidth * 0.5}
                  y2={prismCenter.y}
                  stroke={currentFunction.lightColor}
                  strokeWidth="12"
                  strokeLinecap="round"
                  style={{ filter: 'blur(8px)' }}
                />
                {/* Main beam */}
                <line
                  key={`beam-${selectedFunction}`}
                  className="input-beam"
                  x1={30}
                  y1={prismCenter.y}
                  x2={prismCenter.x - prismWidth * 0.5}
                  y2={prismCenter.y}
                  stroke="url(#beamGradient)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                {/* Beam tip dot */}
                <circle
                  cx={prismCenter.x - prismWidth * 0.5}
                  cy={prismCenter.y}
                  r="5"
                  fill={currentFunction.color}
                  style={{
                    opacity: 0,
                    animation: 'slideUp 0.3s ease-out 0.4s forwards'
                  }}
                />
              </g>
            )}

            {/* Prism group with floating animation */}
            <g
              className={`prism-container ${selectedFunction ? 'active' : 'inactive'}`}
              style={{ '--glow-color': currentFunction?.lightColor || 'rgba(247, 192, 138, 0.3)' }}
            >
              {/* Back/right face */}
              <polygon
                points={`${apex.x},${apex.y} ${bottomRight.x},${bottomRight.y} ${backRight.x},${backRight.y}`}
                fill="url(#glassRight)"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="1"
              />

              {/* Front face - main glass surface */}
              <polygon
                points={`${apex.x},${apex.y} ${bottomLeft.x},${bottomLeft.y} ${bottomRight.x},${bottomRight.y}`}
                fill="url(#glassFront)"
                stroke="rgba(255,255,255,0.4)"
                strokeWidth="1.5"
              />

              {/* Inner edge highlight */}
              <line
                x1={apex.x}
                y1={apex.y}
                x2={bottomRight.x}
                y2={bottomRight.y}
                stroke="rgba(255,255,255,0.7)"
                strokeWidth="2"
              />

              {/* Glass shimmer overlay */}
              <polygon
                className="glass-shimmer"
                points={`${apex.x},${apex.y} ${apex.x - prismWidth * 0.25},${prismCenter.y} ${apex.x},${prismCenter.y + 20}`}
                fill="url(#highlight)"
                opacity="0.4"
              />

              {/* Subtle inner reflection */}
              <ellipse
                cx={prismCenter.x - 10}
                cy={prismCenter.y + 20}
                rx="20"
                ry="10"
                fill="rgba(255,255,255,0.15)"
                transform={`rotate(-15 ${prismCenter.x - 10} ${prismCenter.y + 20})`}
              />
            </g>

            {/* Output rays - energy streams */}
            {selectedFunction && currentFunction.useCases.map((_, index) => {
              const total = currentFunction.useCases.length;
              const ySpread = 240;
              const yStart = prismCenter.y - ySpread / 2;
              const yStep = ySpread / (total - 1);
              const rayY = yStart + index * yStep;

              // Ray exit points staggered on prism face
              const exitY = prismCenter.y - 40 + index * 28;

              return (
                <g key={`ray-group-${selectedFunction}-${index}`}>
                  {/* Ray glow */}
                  <line
                    x1={prismCenter.x + prismWidth * 0.35}
                    y1={exitY}
                    x2={svgWidth - 25}
                    y2={rayY}
                    stroke={currentFunction.lightColor}
                    strokeWidth="8"
                    strokeLinecap="round"
                    style={{
                      filter: 'blur(6px)',
                      opacity: 0,
                      animation: `slideUp 0.4s ease-out ${0.3 + index * 0.1}s forwards`
                    }}
                  />
                  {/* Main ray */}
                  <line
                    className="energy-ray"
                    x1={prismCenter.x + prismWidth * 0.35}
                    y1={exitY}
                    x2={svgWidth - 25}
                    y2={rayY}
                    stroke="url(#rayGradient)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    style={{
                      animationDelay: `${0.25 + index * 0.1}s, ${0.85 + index * 0.1}s`,
                      strokeDasharray: '250'
                    }}
                  />
                  {/* End dot */}
                  <circle
                    cx={svgWidth - 25}
                    cy={rayY}
                    r="4"
                    fill={currentFunction.color}
                    style={{
                      opacity: 0,
                      animation: `slideUp 0.3s ease-out ${0.55 + index * 0.1}s forwards`
                    }}
                  />
                </g>
              );
            })}
          </svg>
        </div>

        {/* RIGHT: Use case labels */}
        <div className="prism-viz-usecases" style={{ display: 'flex', flexDirection: 'column', gap: '14px', justifyContent: 'center' }}>
          {selectedFunction && currentFunction.useCases.map((useCase, index) => (
            <div
              key={`${selectedFunction}-${index}`}
              className="use-case-card"
              style={{
                padding: '12px 18px',
                backgroundColor: '#fff',
                borderLeft: `3px solid ${currentFunction.color}`,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                animationDelay: `${0.4 + index * 0.08}s`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Subtle gradient overlay */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(90deg, ${currentFunction.lightColor} 0%, transparent 30%)`,
                  opacity: 0.3,
                  pointerEvents: 'none',
                }}
              />
              <span style={{ position: 'relative', zIndex: 1 }}>{useCase}</span>
            </div>
          ))}
          {!selectedFunction && (
            <div
              style={{
                textAlign: 'center',
                padding: '20px',
              }}
            >
              <p
                style={{
                  fontSize: '14px',
                  color: '#aaa',
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                Select a function to see<br />relevant use cases
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FunctionPrismVisualization;
